import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import { IconCheck } from '@/icons';
import Button from '../core/Button';
import { CheckboxSection } from './CheckboxSection';
import {
  BobcatTagDTO,
  getGetAllowedOrganizationOfferGroupsQueryKey,
  getGetAllowedOrganizationTagsQueryKey,
  OrganizationDTO,
  useGetAllowedOrganizationOfferGroups,
  useGetAllowedOrganizationTags,
  useGetBobcatTagsList,
  useListBobcatOfferGroups,
  useSetAllowedOrganizationOfferGroups,
  useSetAllowedOrganizationTags,
} from '@/api/trafikportalen';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/useToast';
import { checkUserPermissions } from '../Auth/checkUserPermissions';
import { collator } from '@/utils/collator';
import {
  TRAVELLER_CLASS_PREFIX,
  PRODUCT_CLASS_PREFIX,
} from '@/helpers/ticketClass';

interface OrganizationTicketSettingsProps {
  organization: OrganizationDTO;
}

export const OrganizationTicketSettings = ({
  organization,
}: OrganizationTicketSettingsProps) => {
  const { data: offerGroups, isLoading: isLoadingOfferGroups } =
    useListBobcatOfferGroups();
  const { data: allTags, isLoading: isLoadingTags } = useGetBobcatTagsList();
  const { data: allowedOfferGroups, isLoading: isLoadingAllowedOfferGroups } =
    useGetAllowedOrganizationOfferGroups(organization.id);
  const { data: allowedTags, isLoading: isLoadingAllowedTags } =
    useGetAllowedOrganizationTags(organization.id);
  const setAllowedOfferGroups = useSetAllowedOrganizationOfferGroups();
  const setAllowedTags = useSetAllowedOrganizationTags();
  const queryClient = useQueryClient();
  const [ref] = useAutoAnimateConditional<HTMLFormElement>();
  const { formatMessage } = useIntl();

  const [editMode, setEditMode] = useState(false);
  const [selectedOffers, setSelectedOffers] = useState<string[]>([]);
  const [selectedTravellerClasses, setSelectedTravellerClasses] = useState<
    string[]
  >([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const setStartingSelection = useCallback(() => {
    setSelectedOffers(
      allowedOfferGroups
        ?.filter((offer) => offer.sales_channel !== 'school')
        .map((offer) => offer.id) || [],
    );

    const masterTags =
      allowedTags?.filter((tag) => tag.collection_type === 'master') || [];
    const travellerClasses = masterTags.filter((tag) =>
      tag.tag_id.startsWith(TRAVELLER_CLASS_PREFIX),
    );
    const tags = masterTags.filter((tag) =>
      tag.tag_id.startsWith(PRODUCT_CLASS_PREFIX),
    );

    setSelectedTravellerClasses(travellerClasses.map((tag) => tag.id));
    setSelectedTags(tags.map((tag) => tag.id));
  }, [allowedOfferGroups, allowedTags]);

  useEffect(() => {
    setStartingSelection();
  }, [setStartingSelection]);

  const { travellerClasses, tags } = useMemo<{
    travellerClasses: BobcatTagDTO[];
    tags: BobcatTagDTO[];
  }>(() => {
    if (!allTags) return { travellerClasses: [], tags: [] };

    return {
      travellerClasses: allTags
        .filter((tag) => tag.tag_id.startsWith(TRAVELLER_CLASS_PREFIX))
        .sort((a, b) => collator.compare(a.tag_id, b.tag_id)),
      tags: allTags.filter((tag) =>
        tag.tag_id.startsWith(PRODUCT_CLASS_PREFIX),
      ),
    };
  }, [allTags]);

  const offerGroupsWithTranslatedProductClass = useMemo(() => {
    if (!offerGroups || !tags) return [];

    return offerGroups
      .filter((og) => og.sales_channel !== 'school')
      .map((offer) => {
        const productClassTag = tags.find(
          (tag) => tag.tag_id === offer.product_class,
        );
        return {
          id: offer.id,
          title: offer.title,
          subtitle: productClassTag?.title,
        };
      });
  }, [offerGroups, tags]);

  const onTagSelectionChange = useCallback((ids: string[]) => {
    setSelectedTags(ids);
  }, []);

  const startEdit = useCallback(() => {
    setEditMode(true);
  }, []);

  const cancelEdit = useCallback(() => {
    setStartingSelection();
    setEditMode(false);
  }, [setStartingSelection]);

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      if (setAllowedOfferGroups.isPending || setAllowedTags.isPending) {
        return;
      }

      e.preventDefault();
      const tags = [...selectedTravellerClasses, ...selectedTags];

      await Promise.all([
        setAllowedOfferGroups.mutateAsync({
          organizationID: organization.id,
          data: {
            offers: selectedOffers,
            collection_type: 'master',
          },
        }),
        setAllowedTags.mutateAsync({
          organizationID: organization.id,
          data: {
            tags,
            collection_type: 'master',
          },
        }),
      ])
        .then(() => {
          toast.success(
            {
              title: formatMessage({
                defaultMessage: 'Inställningarna sparades',
              }),
            },
            { autoClose: 3000 },
          );
          queryClient.invalidateQueries({
            queryKey: getGetAllowedOrganizationOfferGroupsQueryKey(
              organization.id,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getGetAllowedOrganizationTagsQueryKey(organization.id),
          });

          setEditMode(false);
        })
        .catch((error) => {
          console.error('Error: ', error);
          toast.error(
            {
              title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
              description: formatMessage({
                defaultMessage:
                  'Försök spara igen senare, eller vänligen kontakta support.',
              }),
            },
            { autoClose: 3000 },
          );
        });
    },
    [
      setAllowedOfferGroups,
      setAllowedTags,
      selectedTravellerClasses,
      selectedTags,
      organization.id,
      selectedOffers,
      formatMessage,
      queryClient,
    ],
  );

  const isPending = setAllowedOfferGroups.isPending || setAllowedTags.isPending;

  const editButtons = (
    <div className="flex justify-end gap-2">
      <Button variant="ghost" disabled={isPending} onClick={cancelEdit}>
        <FormattedMessage defaultMessage="Avbryt" />
      </Button>
      <Button
        type="submit"
        variant="filled"
        loading={isPending}
        startIcon={<IconCheck />}
      >
        <FormattedMessage defaultMessage="Spara" />
      </Button>
    </div>
  );

  return (
    <form ref={ref} onSubmit={onSubmit} className="flex w-full flex-col gap-4">
      <CheckboxSection
        title={formatMessage({ defaultMessage: 'Resenärskategorier' })}
        description={formatMessage({
          defaultMessage:
            'Välj vilka resenärskategorier organisationen ska ha tillgång till. Påverkar både statiska biljetter och biljettkategorier.',
        })}
        items={travellerClasses}
        selectedIds={selectedTravellerClasses}
        onSelectionChange={setSelectedTravellerClasses}
        editMode={editMode}
        onClickButton={startEdit}
        isLoading={isLoadingTags || isLoadingAllowedTags}
        canSeeButton={checkUserPermissions({ permission: 'tenant:editor' })}
        editActions={editButtons}
      />
      <CheckboxSection
        title={formatMessage({ defaultMessage: 'Statiska biljetter' })}
        description={formatMessage({
          defaultMessage:
            'Välj vilka statiska biljetter organisationen ska ha tillgång till.',
        })}
        items={offerGroupsWithTranslatedProductClass}
        selectedIds={selectedOffers}
        onSelectionChange={setSelectedOffers}
        editMode={editMode}
        onClickButton={startEdit}
        isLoading={isLoadingOfferGroups || isLoadingAllowedOfferGroups}
      />
      <CheckboxSection
        title={formatMessage({ defaultMessage: 'Biljettkategorier' })}
        description={formatMessage({
          defaultMessage:
            'Välj vilka biljettkategorier organisationen ska ha tillgång till.',
        })}
        items={tags}
        selectedIds={selectedTags}
        onSelectionChange={onTagSelectionChange}
        editMode={editMode}
        onClickButton={startEdit}
        isLoading={isLoadingTags || isLoadingAllowedTags}
      />
      {editMode && <div className="mt-4">{editButtons}</div>}
    </form>
  );
};
